import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Card, CardContent, CardHeader, Input } from '@agms/ui'
import { firstLoginApi } from '../lib/api'
import { apiErrorMessage } from '../hooks/useErrorHandler'
import agmsLogo from '../assets/logos/agms-logo-on-white.svg'

export function FirstLoginPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = useMemo(() => searchParams.get('token') ?? '', [searchParams])
  const [error, setError] = useState<string | null>(null)

  const invite = useQuery({
    queryKey: ['first-login', token],
    queryFn: () => firstLoginApi.show(token),
    enabled: token.length > 0,
    retry: false,
  })

  const mutation = useMutation({
    mutationFn: ({ password, passwordConfirmation }: { password: string; passwordConfirmation: string }) => (
      firstLoginApi.setPassword(token, password, passwordConfirmation)
    ),
    onSuccess: () => navigate('/setup-2fa', { replace: true }),
    onError: (err) => setError(apiErrorMessage(err, 'Unable to set password.')),
  })

  useEffect(() => {
    document.title = 'Set your password · AGMS CRM'
  }, [])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)
    const form = new FormData(event.currentTarget)
    mutation.mutate({
      password: String(form.get('password') ?? ''),
      passwordConfirmation: String(form.get('password_confirmation') ?? ''),
    })
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-4 py-12 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md overflow-hidden">
        <div className="h-1 bg-accent-500" />
        <CardHeader className="items-center text-center">
          <img src={agmsLogo} alt="AGMS" className="h-12 w-auto" />
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Set your password</h1>
          <p className="mt-2 text-sm text-gray-600">
            {invite.data ? `${invite.data.name} · ${invite.data.email}` : 'AGMS CRM account setup'}
          </p>
        </CardHeader>
        <CardContent>
          {!token && <p className="text-sm text-red-600">Invite token is missing.</p>}
          {invite.error && <p className="text-sm text-red-600">This invite link is invalid or expired.</p>}
          {invite.data && (
            <form className="space-y-4" onSubmit={handleSubmit}>
              <Input name="password" label="New password" type="password" minLength={12} required />
              <Input name="password_confirmation" label="Confirm password" type="password" minLength={12} required />
              {error && <p className="text-sm text-red-600">{error}</p>}
              <Button type="submit" className="w-full" isLoading={mutation.isPending}>
                Continue to 2FA Setup
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </main>
  )
}
